import { useEffect } from 'react';
import SEOHead from '../../components/feature/SEOHead';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function PoliticaPrivacidadePage() {
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Política de Privacidade - IBX Brasil",
    "description": "Política de privacidade e proteção de dados da IBX Brasil, em conformidade com a LGPD.",
    "url": "https://ibxbrasil.com.br/politica-de-privacidade"
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Política de Privacidade | IBX Brasil"
        description="Conheça nossa política de privacidade e como protegemos seus dados pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD)."
        canonical="https://ibxbrasil.com.br/politica-de-privacidade"
        ogImage="https://readdy.ai/api/search-image?query=data%20privacy%20and%20protection%20policy%20document%20with%20LGPD%20compliance%20and%20security%20icons&width=1200&height=630&seq=ibx-privacidade-og&orientation=landscape"
        schema={webPageSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-6">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Política de Privacidade
              </h1>
              <p className="text-gray-600 text-lg">
                Última atualização: 20 de janeiro de 2025
              </p>
            </div>

            {/* Introdução */}
            <section className="mb-12">
              <p className="text-gray-700 leading-relaxed mb-4">
                A <strong>IBX Brasil Consultoria em Seguros</strong> está comprometida com a proteção da privacidade e dos dados pessoais de seus clientes, parceiros e visitantes do site. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais, em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)</strong>.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Ao utilizar nossos serviços ou acessar nosso site, você concorda com os termos desta política.
              </p>
            </section>

            {/* 1. Identificação do Controlador */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">1</span>
                Identificação do Controlador de Dados
              </h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Razão Social:</strong> IBX Brasil Consultoria em Seguros</p>
                <p className="text-gray-700 mb-2"><strong>CNPJ:</strong> [Inserir CNPJ]</p>
                <p className="text-gray-700 mb-2"><strong>Endereço:</strong> [Inserir endereço completo]</p>
                <p className="text-gray-700 mb-2"><strong>E-mail:</strong> contato@ibxbrasil.com.br</p>
                <p className="text-gray-700"><strong>Telefone:</strong> (11) 98765-4321</p>
              </div>
            </section>

            {/* 2. Dados Coletados */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">2</span>
                Dados Pessoais Coletados
              </h2>
              <p className="text-gray-700 mb-4">
                Coletamos as seguintes categorias de dados pessoais:
              </p>
              
              <div className="space-y-4">
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">Dados de Identificação</h3>
                  <p className="text-gray-700">Nome completo, CPF/CNPJ, RG, data de nascimento, endereço, telefone, e-mail.</p>
                </div>
                
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">Dados Profissionais</h3>
                  <p className="text-gray-700">Nome da empresa, cargo, setor de atuação, informações sobre contratos de seguros.</p>
                </div>
                
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">Dados de Navegação</h3>
                  <p className="text-gray-700">Endereço IP, tipo de navegador, páginas visitadas, tempo de permanência, cookies e tecnologias similares.</p>
                </div>
                
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">Dados de Comunicação</h3>
                  <p className="text-gray-700">Mensagens enviadas através de formulários de contato, e-mails, WhatsApp e chat online.</p>
                </div>
              </div>
            </section>

            {/* 3. Finalidade do Tratamento */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">3</span>
                Finalidade do Tratamento de Dados
              </h2>
              <p className="text-gray-700 mb-4">
                Utilizamos seus dados pessoais para as seguintes finalidades:
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Prestação de serviços:</strong> Consultoria em seguros, gestão de contratos, análise de riscos e recuperação de sinistros.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Comunicação:</strong> Responder solicitações, enviar propostas comerciais, newsletters e atualizações sobre serviços.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Cumprimento de obrigações legais:</strong> Atender exigências regulatórias e fiscais do setor de seguros.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Melhoria de serviços:</strong> Análise de dados para aprimorar a experiência do usuário e desenvolver novos produtos.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Marketing:</strong> Envio de ofertas personalizadas e conteúdos relevantes (mediante consentimento).</span>
                </li>
              </ul>
            </section>

            {/* 4. Base Legal */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">4</span>
                Base Legal para o Tratamento
              </h2>
              <p className="text-gray-700 mb-4">
                O tratamento de seus dados pessoais é realizado com base nas seguintes hipóteses legais previstas na LGPD:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg space-y-3">
                <p className="text-gray-700"><strong>• Consentimento:</strong> Quando você autoriza expressamente o uso de seus dados.</p>
                <p className="text-gray-700"><strong>• Execução de contrato:</strong> Para cumprir obrigações contratuais de prestação de serviços.</p>
                <p className="text-gray-700"><strong>• Obrigação legal:</strong> Para atender exigências regulatórias do setor de seguros.</p>
                <p className="text-gray-700"><strong>• Legítimo interesse:</strong> Para melhorar nossos serviços e proteger direitos em processos judiciais.</p>
              </div>
            </section>

            {/* 5. Compartilhamento de Dados */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">5</span>
                Compartilhamento de Dados
              </h2>
              <p className="text-gray-700 mb-4">
                Seus dados pessoais podem ser compartilhados com:
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Seguradoras parceiras:</strong> Para cotação, contratação e gestão de apólices de seguros.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Prestadores de serviços:</strong> Empresas de tecnologia, armazenamento de dados e ferramentas de comunicação.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Autoridades públicas:</strong> Quando exigido por lei ou ordem judicial.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Parceiros comerciais:</strong> Mediante consentimento prévio para ofertas e promoções.</span>
                </li>
              </ul>
              
              <div className="mt-4 bg-orange-50 border-l-4 border-orange-500 p-4">
                <p className="text-gray-700">
                  <strong>Importante:</strong> Todos os terceiros com quem compartilhamos dados são obrigados a manter o mesmo nível de proteção e confidencialidade exigido pela LGPD.
                </p>
              </div>
            </section>

            {/* 6. Direitos do Titular */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">6</span>
                Direitos do Titular dos Dados
              </h2>
              <p className="text-gray-700 mb-4">
                De acordo com a LGPD, você possui os seguintes direitos:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-eye-line text-orange-500 mr-2"></i>
                    Confirmação e Acesso
                  </h3>
                  <p className="text-gray-700 text-sm">Confirmar se tratamos seus dados e solicitar acesso a eles.</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-edit-line text-orange-500 mr-2"></i>
                    Correção
                  </h3>
                  <p className="text-gray-700 text-sm">Solicitar correção de dados incompletos, inexatos ou desatualizados.</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-delete-bin-line text-orange-500 mr-2"></i>
                    Eliminação
                  </h3>
                  <p className="text-gray-700 text-sm">Solicitar exclusão de dados tratados com base em consentimento.</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-download-line text-orange-500 mr-2"></i>
                    Portabilidade
                  </h3>
                  <p className="text-gray-700 text-sm">Solicitar transferência de dados a outro fornecedor de serviços.</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-close-circle-line text-orange-500 mr-2"></i>
                    Revogação do Consentimento
                  </h3>
                  <p className="text-gray-700 text-sm">Retirar consentimento a qualquer momento.</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-information-line text-orange-500 mr-2"></i>
                    Informação sobre Compartilhamento
                  </h3>
                  <p className="text-gray-700 text-sm">Saber com quem compartilhamos seus dados.</p>
                </div>
              </div>
              
              <div className="mt-6 bg-orange-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-2">Como exercer seus direitos?</h3>
                <p className="text-gray-700 mb-2">
                  Para exercer qualquer um desses direitos, entre em contato conosco através de:
                </p>
                <p className="text-gray-700">
                  <strong>E-mail:</strong> privacidade@ibxbrasil.com.br<br />
                  <strong>Telefone:</strong> (11) 98765-4321
                </p>
                <p className="text-gray-700 mt-2 text-sm">
                  Responderemos sua solicitação em até 15 dias úteis.
                </p>
              </div>
            </section>

            {/* 7. Retenção de Dados */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">7</span>
                Retenção e Armazenamento de Dados
              </h2>
              <p className="text-gray-700 mb-4">
                Mantemos seus dados pessoais apenas pelo tempo necessário para cumprir as finalidades descritas nesta política, respeitando os seguintes prazos:
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-time-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Dados de clientes ativos:</strong> Durante a vigência do contrato e por até 5 anos após o término, conforme exigências legais.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Dados de prospects:</strong> Por até 2 anos após o último contato, salvo consentimento para período maior.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-orange-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Dados de navegação:</strong> Por até 12 meses, conforme legislação aplicável.</span>
                </li>
              </ul>
              
              <p className="text-gray-700 mt-4">
                Após o término desses prazos, os dados serão eliminados de forma segura, exceto quando houver obrigação legal de retenção.
              </p>
            </section>

            {/* 8. Segurança */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">8</span>
                Segurança dos Dados
              </h2>
              <p className="text-gray-700 mb-4">
                Adotamos medidas técnicas e organizacionais para proteger seus dados contra acessos não autorizados, perda, destruição ou alteração:
              </p>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <i className="ri-lock-line text-orange-500 text-4xl mb-2"></i>
                  <h3 className="font-bold text-gray-900 mb-1">Criptografia</h3>
                  <p className="text-gray-700 text-sm">Dados sensíveis protegidos com criptografia SSL/TLS.</p>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <i className="ri-shield-check-line text-orange-500 text-4xl mb-2"></i>
                  <h3 className="font-bold text-gray-900 mb-1">Controle de Acesso</h3>
                  <p className="text-gray-700 text-sm">Acesso restrito apenas a colaboradores autorizados.</p>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <i className="ri-server-line text-orange-500 text-4xl mb-2"></i>
                  <h3 className="font-bold text-gray-900 mb-1">Backup Seguro</h3>
                  <p className="text-gray-700 text-sm">Cópias de segurança regulares em servidores protegidos.</p>
                </div>
              </div>
            </section>

            {/* 9. Cookies */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">9</span>
                Uso de Cookies e Tecnologias Similares
              </h2>
              <p className="text-gray-700 mb-4">
                Utilizamos cookies e tecnologias similares para melhorar sua experiência de navegação. Os cookies são pequenos arquivos armazenados no seu dispositivo que nos ajudam a:
              </p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-orange-500 mr-2 mt-1"></i>
                  <span className="text-gray-700">Lembrar suas preferências e configurações</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-orange-500 mr-2 mt-1"></i>
                  <span className="text-gray-700">Analisar o tráfego e comportamento dos usuários</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-orange-500 mr-2 mt-1"></i>
                  <span className="text-gray-700">Personalizar conteúdo e anúncios</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-orange-500 mr-2 mt-1"></i>
                  <span className="text-gray-700">Garantir a segurança do site</span>
                </li>
              </ul>
              
              <p className="text-gray-700">
                Você pode gerenciar ou desativar cookies através das configurações do seu navegador. No entanto, isso pode afetar algumas funcionalidades do site.
              </p>
            </section>

            {/* 10. Transferência Internacional */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">10</span>
                Transferência Internacional de Dados
              </h2>
              <p className="text-gray-700">
                Alguns de nossos prestadores de serviços podem estar localizados fora do Brasil. Nesses casos, garantimos que a transferência internacional de dados seja realizada em conformidade com a LGPD, adotando cláusulas contratuais padrão e outras salvaguardas adequadas para proteger suas informações.
              </p>
            </section>

            {/* 11. Menores de Idade */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">11</span>
                Dados de Menores de Idade
              </h2>
              <p className="text-gray-700">
                Nossos serviços são destinados a pessoas maiores de 18 anos. Não coletamos intencionalmente dados de menores de idade sem o consentimento dos pais ou responsáveis legais. Caso identifiquemos que coletamos dados de menores sem autorização, tomaremos medidas para excluí-los imediatamente.
              </p>
            </section>

            {/* 12. Alterações */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">12</span>
                Alterações nesta Política
              </h2>
              <p className="text-gray-700">
                Esta Política de Privacidade pode ser atualizada periodicamente para refletir mudanças em nossas práticas ou na legislação aplicável. Recomendamos que você revise esta página regularmente. A data da última atualização está indicada no início deste documento.
              </p>
            </section>

            {/* 13. Contato e DPO */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">13</span>
                Contato e Encarregado de Dados (DPO)
              </h2>
              <p className="text-gray-700 mb-4">
                Para dúvidas, solicitações ou reclamações relacionadas à proteção de dados pessoais, entre em contato com nosso Encarregado de Proteção de Dados (DPO):
              </p>
              
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-6 rounded-lg border-l-4 border-orange-500">
                <h3 className="font-bold text-gray-900 mb-3">Encarregado de Proteção de Dados (DPO)</h3>
                <div className="space-y-2">
                  <p className="text-gray-700 flex items-center">
                    <i className="ri-mail-line text-orange-500 mr-3"></i>
                    <strong>E-mail:</strong>&nbsp;privacidade@ibxbrasil.com.br
                  </p>
                  <p className="text-gray-700 flex items-center">
                    <i className="ri-phone-line text-orange-500 mr-3"></i>
                    <strong>Telefone:</strong>&nbsp;(11) 98765-4321
                  </p>
                  <p className="text-gray-700 flex items-center">
                    <i className="ri-map-pin-line text-orange-500 mr-3"></i>
                    <strong>Endereço:</strong>&nbsp;[Inserir endereço completo]
                  </p>
                </div>
              </div>
              
              <p className="text-gray-700 mt-4 text-sm">
                Você também pode registrar reclamações junto à Autoridade Nacional de Proteção de Dados (ANPD) através do site: <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">www.gov.br/anpd</a>
              </p>
            </section>

            {/* CTA Final */}
            <section className="mt-16 bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-xl text-center">
              <h2 className="text-2xl font-bold text-white mb-4">
                Ainda tem dúvidas sobre privacidade?
              </h2>
              <p className="text-gray-300 mb-6">
                Nossa equipe está pronta para esclarecer qualquer questão sobre o tratamento dos seus dados.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contato"
                  className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                >
                  <i className="ri-mail-line mr-2"></i>
                  Entrar em Contato
                </a>
                <a
                  href="/faq"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-question-line mr-2"></i>
                  Ver Perguntas Frequentes
                </a>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
